import { IonBackButton, IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol, IonContent, IonGrid, IonPage, IonRow, IonTitle, isPlatform } from "@ionic/react";
import { FormControl, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { RouteComponentProps } from "react-router";
import { CustomLoading, Header } from "../../components";
import { db } from "../../data/db/db";
import { ArticleSpecs } from "../../models/articleModel";
import { URLParamToArray } from "../article/articleHelper";

import "./CuttingDataPreselection.scss";
import { useTranslation } from "react-i18next";

interface PreselectionForm {
    diameter: string,
    variation: string,
    strategy: string
}

interface CuttingDataPreselectionProps extends RouteComponentProps<{
    id: string
}> { }

const CuttingDataPreselection: React.FC<CuttingDataPreselectionProps> = ({ history, match }) => {
    const articleStr = match.params.id;
    const [ident, setIdent] = useState<string[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [diameters, setDiameters] = useState<string[]>([]);
    let [variantOptions, setVariantOptions] = useState<ArticleSpecs[]>([]);
    const { t } = useTranslation()

    const { control, handleSubmit, watch, setValue } = useForm<PreselectionForm>({
        mode: "onTouched",
        reValidateMode: "onChange"
    });
    const selectedDiameter = watch("diameter")



    const onSubmit: SubmitHandler<PreselectionForm> = data => {
        //console.log(data)
        if (data) {
            let noSpaces = data.variation.split(" ").join("")
            let [ident, pos] = noSpaces.split("-");
            //console.log(pos)
            if (data.strategy === "std") {
                history.push(`/tabs/calculator/preselection/cuttingDataStd/${ident}/${pos}`)
            } else {
                history.push(`/tabs/calculator/preselection/cuttingDataTro/${ident}/${pos}`)
            }
        }
    }

    useEffect(() => {
        setIdent(URLParamToArray(articleStr))

        return () => {
            //unmount
            setIdent(null);
        }

    }, [articleStr])


    const articles = useLiveQuery(
        async () => {
            if (ident) {
                setLoading(true);
                const articles = await db.articlesSpec
                    .where('ident')
                    .anyOf(ident)
                    .toArray()
                setLoading(false)
                // Return result
                return articles;
            }
        },
        [ident]
    );

    useEffect(() => {
        let tempDiameters: string[] = [];
        articles?.forEach(article => {
            if (!tempDiameters.includes(article.d1.toString())) {
                tempDiameters.push(article.d1.toString())
            }
        })
        setDiameters(tempDiameters);
        if (articles) {
            setVariantOptions(articles);
        }
    }, [articles])


    useEffect(() => {
        if (articles && selectedDiameter) {
            if (selectedDiameter !== "alle") {
                let temp = articles.filter(article => {
                    return article.d1.toString() === selectedDiameter;
                })
                setValue("variation", temp[0].ident + "-" + temp[0].pos)
                setVariantOptions(temp)
            } else {
                setVariantOptions(articles);
            }
        }
    }, [selectedDiameter, articles, setValue])

    return (
        <IonPage>
            <Header>
                <IonButtons slot="start">
                    <IonBackButton defaultHref="/tabs/calculator" text={isPlatform('ios') ? t("Zurück") : undefined} />
                </IonButtons>
                <IonTitle>{t("cuttingDataPreselection.Vorauswahl")}</IonTitle>
            </Header>
            <IonContent>
                <IonGrid fixed={true} className="mainContent">
                    <IonRow>
                        <IonCol size="12">
                            <IonCard style={{ textAlign: "left", marginTop: 20 }}>
                                <IonCardHeader>
                                    {ident && <IonCardTitle >{ident.length > 1 ? ident[0] + " / " + ident[1] : ident}</IonCardTitle>}
                                </IonCardHeader>
                                <IonCardContent>
                                    {articles && articles.length > 0 && variantOptions.length > 0 &&
                                        <form >
                                            <div className="preselectionForm">
                                                {/* Durchmesser */}
                                                <Controller
                                                    render={({ field: { onChange, onBlur, value } }) => (
                                                        <FormControl variant="standard" sx={{ m: 1, minWidth: "calc(100% - 16px)", maxWidth: "calc(100% - 16px)" }}>
                                                            <InputLabel id="durchmesser">{t("cuttingDataPreselection.Durchmesser")}</InputLabel>
                                                            <Select
                                                                onChange={onChange}
                                                                onBlur={onBlur}
                                                                value={value}
                                                                variant="standard"
                                                                labelId="durchmesser"
                                                            >
                                                                <MenuItem value="alle">{t("cuttingDataPreselection.Alle")}</MenuItem>
                                                                {diameters.map((diameter) => (
                                                                    <MenuItem
                                                                        key={diameter}
                                                                        value={diameter}
                                                                    >{diameter} mm</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                    control={control}
                                                    name="diameter"
                                                    defaultValue={"alle"}
                                                    rules={{
                                                        required: { value: true, message: t("Feld darf nicht leer sein") },
                                                    }}
                                                />

                                                {/* Artikel-Variante */}
                                                <Controller
                                                    render={({ field: { onChange, onBlur, value } }) => (
                                                        <FormControl variant="standard" sx={{ m: 1, minWidth: "calc(100% - 16px)", maxWidth: "calc(100% - 16px)", margin: "8px 0" }}>
                                                            <InputLabel id="position">{t("cuttingDataPreselection.Position")}</InputLabel>
                                                            <Select
                                                                onChange={onChange}
                                                                onBlur={onBlur}
                                                                value={value}
                                                                variant="standard"
                                                                labelId="position"
                                                            >
                                                                {variantOptions.map((variant) => (
                                                                    <MenuItem
                                                                        key={variant.id}
                                                                        value={variant.ident + "-" + variant.pos}
                                                                    >{variant.ident + "-" + variant.pos + " (d1:" + variant.d1 + ", l2:" + variant.l2 + ", l1:" + variant.l1 + ")"}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                    control={control}
                                                    name="variation"
                                                    defaultValue={articles[0].ident + "-" + articles[0].pos}
                                                    rules={{
                                                        required: { value: true, message: t("Feld darf nicht leer sein") },
                                                    }}
                                                />


                                                {/* Bearbeitungs-Strategie */}
                                                <Controller
                                                    render={({ field: { onChange, value } }) => (
                                                        <ToggleButtonGroup
                                                            value={value}
                                                            exclusive
                                                            size="small"
                                                            onChange={onChange}
                                                            aria-label="text alignment"
                                                            sx={{ m: 1, marginTop: "8px" }}
                                                            disabled={articles[0].trocut === "1" ? false : true}
                                                        >
                                                            <ToggleButton value="std" aria-label="left aligned">
                                                                {t("cuttingDataPreselection.Herkömmlich")}
                                                            </ToggleButton>
                                                            <ToggleButton value="tro" aria-label="right aligned">
                                                            {t("cuttingDataPreselection.Trochoidal")}
                                                            </ToggleButton>
                                                        </ToggleButtonGroup>
                                                    )}
                                                    name="strategy"
                                                    control={control}
                                                    defaultValue={"std"}
                                                />
                                            </div>
                                            {/* submit */}
                                            <IonButton style={{ marginTop: 24 }} onClick={handleSubmit(onSubmit)} expand="block">{t("cuttingDataPreselection.OK")}</IonButton>


                                        </form>}
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                    </IonRow>
                </IonGrid>

            </IonContent>
            <CustomLoading isOpen={loading} />
        </IonPage>
    )
}

export default CuttingDataPreselection;